import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  useBuilding,
  ALL_BUILDING,
  COMPANY_BUILDING,
  Building,
} from "../../context/BuildingContext";
import { useOwner } from "../../context/OwnerContext";
import { buildingsApi } from "../../services/api";

const SEGMENT_LABELS: Record<string, string> = {
  buildings: "Buildings",
  units: "Units",
  expenses: "Expenses",
  invoices: "Invoices",
  payments: "Payments",
  journal: "Journal",
  budgets: "Budgets",
  statements: "Statements",
  reports: "Reports",
  users: "Users",
  settings: "Settings",
  new: "New",
  dashboard: "Dashboard",
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function segmentLabel(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  if (UUID_RE.test(segment)) return "Details";
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

interface Crumb {
  label: string;
  to: string;
}

const BreadcrumbBar: React.FC = () => {
  const { currentBuilding, setCurrentBuilding } = useBuilding();
  const { owner } = useOwner();
  const location = useLocation();
  const [buildings, setBuildings] = useState<Building[]>([]);

  useEffect(() => {
    buildingsApi
      .getAll()
      .then((data: any) => {
        if (Array.isArray(data)) setBuildings(data as Building[]);
      })
      .catch(() => {
        /* silently ignore — breadcrumb is non-critical */
      });
  }, []);

  const handleBuildingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    if (value === "__ALL__") {
      setCurrentBuilding(ALL_BUILDING);
    } else if (value === "COMPANY") {
      setCurrentBuilding(COMPANY_BUILDING);
    } else {
      const found = buildings.find((b) => b.id === value) ?? null;
      if (found) setCurrentBuilding(found);
    }
  };

  // Strip /dashboard prefix and split into segments
  const path = location.pathname.replace(/^\/dashboard\/?/, "");
  const segments = path ? path.split("/").filter(Boolean) : [];

  const crumbs: Crumb[] = [];

  // Build cumulative path crumbs for sections
  let cumulativePath = "/dashboard";
  for (const segment of segments) {
    cumulativePath += `/${segment}`;
    crumbs.push({ label: segmentLabel(segment), to: cumulativePath });
  }

  // On root /dashboard always show a "Dashboard" crumb after the dropdown
  const allCrumbs: Crumb[] =
    crumbs.length === 0 ? [{ label: "Dashboard", to: "/dashboard" }] : crumbs;

  const selectedValue = currentBuilding?.id ?? "__ALL__";

  return (
    <nav
      className="flex items-center space-x-1 text-sm py-2 mb-4 overflow-x-auto whitespace-nowrap scrollbar-hide"
      aria-label="Breadcrumb"
    >
      {/* Owner label — always visible */}
      {owner?.name && (
        <>
          <span className="text-primary-700 font-semibold text-sm truncate max-w-[180px]">
            {owner.name}
          </span>
          <ChevronRightIcon
            className="h-4 w-4 flex-shrink-0 text-gray-400"
            aria-hidden="true"
          />
        </>
      )}

      {/* Building picker — always visible */}
      <select
        value={selectedValue}
        onChange={handleBuildingChange}
        className="border-0 bg-transparent text-primary-700 font-semibold text-sm focus:ring-0 focus:outline-none cursor-pointer pr-1 max-w-[180px] truncate"
        aria-label="Select building"
      >
        <option value="__ALL__">All Buildings</option>
        <option value="COMPANY">Company</option>
        {buildings.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>

      {/* Separator + path crumbs */}
      {allCrumbs.map((crumb, index) => {
        const isLast = index === allCrumbs.length - 1;
        return (
          <React.Fragment key={crumb.to}>
            <ChevronRightIcon
              className="h-4 w-4 flex-shrink-0 text-gray-400"
              aria-hidden="true"
            />
            {isLast ? (
              <span className="text-gray-700 font-medium truncate max-w-[160px]">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.to}
                className="text-gray-500 hover:text-gray-700 truncate max-w-[120px]"
              >
                {crumb.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default BreadcrumbBar;
